import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';

const dropdownRoot = typeof document !== 'undefined' ? document.getElementById('dropdown-root') : null;

if (!dropdownRoot) {
    console.warn("Element with ID 'dropdown-root' not found. Ensure it's in your public/index.html for React Portals.");
}

const calculatePosition = (
  target: HTMLElement,
  menu: HTMLElement,
  dropDirection?: 'up' | 'down',
  alignEnd?: boolean
): React.CSSProperties => {
  const targetRect = target.getBoundingClientRect();
  const menuRect = menu.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;

  const spaceBelow = viewportHeight - targetRect.bottom;
  const spaceAbove = targetRect.top;

  const shouldDropUp = dropDirection
    ? dropDirection === 'up'
    : (targetRect.bottom + menuRect.height > viewportHeight && targetRect.top > menuRect.height);

  const maxHeight = shouldDropUp ? spaceAbove - 10 : spaceBelow - 10; 
  const top = shouldDropUp
    ? scrollY + targetRect.top - Math.min(menuRect.height, maxHeight)
    : scrollY + targetRect.bottom;

  const left = alignEnd
    ? scrollX + targetRect.right - menuRect.width
    : scrollX + targetRect.left;

  return {
    position: 'absolute',
    top: `${top}px`,
    left: `${left}px`,
    zIndex: 1000,
    backgroundColor: 'white',
    border: '1px solid #ccc',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    minWidth: `${targetRect.width}px`,
    maxHeight: `${maxHeight}px`,
    overflowY: 'auto',
    borderRadius: '0.5rem',
  };
};


const DropdownPortal: React.FC<{
    isOpen: boolean;
    targetRef: React.RefObject<HTMLElement>;
    onClose: () => void;
    children: React.ReactNode;
    dropDirection?: 'up' | 'down';
    alignEnd?: boolean;
}> = ({ isOpen, targetRef, onClose, children, dropDirection, alignEnd }) => {
    const dropdownMenuRef = useRef<HTMLDivElement>(null);
    const [positionStyle, setPositionStyle] = useState<React.CSSProperties>({});

    const updatePosition = () => {
        if (targetRef.current && dropdownMenuRef.current) {
            const newStyle = calculatePosition(
                targetRef.current,
                dropdownMenuRef.current,
                dropDirection,
                alignEnd
            );
            setPositionStyle(newStyle);
        }
    };

    useLayoutEffect(() => {
        if (!isOpen || !targetRef.current || !dropdownMenuRef.current) return;
        updatePosition();
    }, [isOpen, targetRef, dropDirection, alignEnd]);

    useEffect(() => {
        if (!isOpen || !targetRef.current || !dropdownMenuRef.current) return undefined;

        const target = targetRef.current;

        const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(() => {
                updatePosition();
            });
        });
        resizeObserver.observe(target);

        window.addEventListener('resize', updatePosition);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen]);

  
useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownMenuRef.current &&
      !dropdownMenuRef.current.contains(event.target as Node) &&
      targetRef.current &&
      !targetRef.current.contains(event.target as Node)
    ) {
      onClose();
    }
  };

  const handleScroll = (event: Event) => {
    if (
      dropdownMenuRef.current &&
      targetRef.current &&
      !dropdownMenuRef.current.contains(event.target as Node) &&
      !targetRef.current.contains(event.target as Node)
    ) {
      onClose();
    }
  };

  if (isOpen) {
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
  }

  return () => {
    document.removeEventListener('mousedown', handleClickOutside);
    window.removeEventListener('scroll', handleScroll, true);
  };
}, [isOpen, onClose, targetRef]);


    if (!isOpen || !dropdownRoot) return null;

    return ReactDOM.createPortal(
        <div ref={dropdownMenuRef} className="dropdown-menu show" style={positionStyle}>
            {children}
        </div>,
        dropdownRoot
    );
};

export default DropdownPortal;
